<template>
  <div v-if="roles.includes('INSTANCE_ADMIN') || roles.includes('SUPER_USER')">
    <!-- RAIL MODE: Flyout with stats -->
    <v-menu
      v-if="rail"
      v-model="flyoutOpen"
      location="end"
      :close-on-content-click="false"
      :open-on-hover="!isTouchDevice"
      :open-on-click="isTouchDevice"
      :open-delay="FLYOUT_OPEN_DELAY"
      :close-delay="FLYOUT_CLOSE_DELAY"
    >
      <template #activator="{ props }">
        <div
          v-bind="props"
          class="collapsed-icons stats-flyout-activator mx-1"
          :aria-label="$lang.labels.instances"
          :aria-expanded="flyoutOpen"
          aria-haspopup="true"
          role="button"
          tabindex="0"
          @keydown.enter="flyoutOpen = !flyoutOpen"
          @keydown.space.prevent="flyoutOpen = !flyoutOpen"
        >
          <div class="icon-container pb-1">
            <v-icon color="primary">
              mdi-chip
            </v-icon>
          </div>
          <div class="icon-container rail-spacing pb-1">
            <v-icon color="accent">
              mdi-sd
            </v-icon>
          </div>
          <div class="icon-container rail-spacing">
            <v-icon color="success">
              mdi-database
            </v-icon>
          </div>
        </div>
      </template>

      <!-- Flyout content: Compact stats view -->
      <v-card class="stats-flyout-menu">
        <v-list-subheader class="flyout-header">
          {{ $lang.labels.instances }}
        </v-list-subheader>
        <v-card-text class="pa-3">
          <div v-if="loading" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="24"
            />
          </div>
          <div v-else-if="error" class="text-red text-center text-caption">
            Error fetching data
          </div>
          <div v-else class="flyout-stats">
            <!-- CPU -->
            <div class="stat-item pb-2">
              <v-icon color="primary" size="small">
                mdi-chip
              </v-icon>
              <v-progress-linear
                :model-value="averageCpuUsage"
                color="primary"
                height="6"
                class="mx-2 flex-grow-1"
                style="border-radius: 30px !important"
              />
              <span class="stat-value">{{ formatCpu(averageCpuUsage) }}%</span>
            </div>
            <!-- Memory -->
            <div class="stat-item pb-2">
              <v-icon color="accent" size="small">
                mdi-sd
              </v-icon>
              <v-progress-linear
                :model-value="averageMemoryUsage"
                color="accent"
                height="6"
                class="mx-2 flex-grow-1"
                style="border-radius: 30px !important"
              />
              <span class="stat-value">{{ formatMemory(averageMemoryUsed) }} GB</span>
            </div>
            <!-- Database -->
            <div class="stat-item">
              <v-icon color="success" size="small">
                mdi-database
              </v-icon>
              <template v-if="dbSizeGB">
                <v-progress-linear
                  :model-value="usedDBMemoryGB"
                  :max="dbSizeGB"
                  color="success"
                  height="6"
                  class="mx-2 flex-grow-1"
                  style="border-radius: 30px !important"
                />
                <span class="stat-value">{{ formatMemory(usedDBMemoryGB) }} GB</span>
              </template>
              <template v-else>
                <span class="stat-value ms-2">{{ formatMemory(usedDBMemoryGB) }} GB</span>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>

    <!-- EXPANDED MODE: Original expansion panels -->
    <v-expansion-panels
      v-else
      v-model="expandedPanels"
      class="stats-panel mx-1"
      multiple
      variant="accordion"
    >
      <v-expansion-panel>
        <!-- Collapsed View -->
        <v-expansion-panel-title
          class="rail-title d-flex align-center justify-space-between"
        >
          <template #actions="{ expanded }">
            <v-icon>
              {{ expanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
            </v-icon>
          </template>
          <div
            v-if="expandedPanels.length === 0"
            class="collapsed-stats"
          >
            <div class="stat-item pb-1">
              <v-icon color="primary">
                mdi-chip
              </v-icon>
              <v-progress-linear
                :model-value="averageCpuUsage"
                color="primary"
                height="6"
                style="border-radius: 30px !important; flex: 1; margin: 0 4px"
              />
              <span class="stat-value">{{ formatCpu(averageCpuUsage) }}%</span>
            </div>
            <div class="stat-item mb-1">
              <v-icon color="accent">
                mdi-sd
              </v-icon>
              <v-progress-linear
                :model-value="averageMemoryUsage"
                color="accent"
                height="6"
                style="border-radius: 30px !important; flex: 1; margin: 0 4px"
              />
              <span class="stat-value">{{ formatMemory(averageMemoryUsed) }} GB</span>
            </div>
            <div class="stat-item">
              <v-icon color="success">
                mdi-database
              </v-icon>

              <template v-if="dbSizeGB">
                <v-progress-linear
                  :model-value="usedDBMemoryGB"
                  :max="dbSizeGB"
                  color="success"
                  height="6"
                  style="border-radius: 30px !important; flex: 1; margin: 0 4px"
                />
                <span class="stat-value">
                  {{ formatMemory(usedDBMemoryGB) }} GB
                </span>
              </template>

              <template v-else>
                <span class="stat-value ms-1">
                  <span class="text-subtitle-2">{{ $lang.header.usedDbMemory }}:</span> {{ formatMemory(usedDBMemoryGB) }} GB
                </span>
              </template>
            </div>
          </div>
          <div v-else class="expanded-header d-flex align-center justify-space-between w-100">
            <span class="header-text">{{ $lang.labels.instances }}</span>
          </div>
        </v-expansion-panel-title>

        <!-- Expanded View -->
        <v-expansion-panel-text class="expansion-panel-padding">
          <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="error" class="text-red text-center">
            Error fetching data
          </div>
          <div v-else>
            <div
              v-for="(instance, index) in statistics"
              :key="index"
              class="instance-stat"
            >
              <h5 class="text-primary">
                <router-link
                  :to="{ name: 'logInstanceStats', params: { id: instance.id } }"
                  class="text-decoration-none color-primary"
                >
                  {{ formatInstanceId(instance.instanceId) }} - {{ $t(`status.${instance.type}`) }}
                </router-link>
              </h5>

              <!-- CPU Usage Section -->
              <div class="stat-title">
                <v-icon color="primary" class="me-1">
                  mdi-chip
                </v-icon>
                CPU
              </div>
              <div class="stat-container">
                <v-tooltip
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-progress-linear
                      :model-value="calculateProgressCPU(instance.cpu)"
                      color="primary"
                      v-bind="props"
                      height="8"
                      style="border-radius: 30px !important"
                    />
                    <span class="stat-value">{{ formatCpu(instance.cpu.average) }}%</span>
                  </template>
                  <span>{{ formatCpu(instance.cpu.average) }}% used of 100%</span>
                </v-tooltip>
              </div>
              <div class="stat-minmax pt-1">
                Min: {{ formatCpu(instance.cpu.min) }}% | Max: {{ formatCpu(instance.cpu.max) }}%
              </div>

              <!-- Memory Usage Section -->
              <div class="stat-title d-flex align-center">
                <div class="d-flex align-center">
                  <v-icon color="accent" class="me-1">
                    mdi-sd
                  </v-icon>
                  Memory
                </div>
                <div class="stat-minmax ms-1">
                  ( {{ formatMemory(convertToGB(instance.memory.total)) }} GB )
                </div>
              </div>
              <div class="stat-container">
                <v-tooltip
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-progress-linear
                      :model-value="calculateProgress(instance.memory)"
                      color="accent"
                      v-bind="props"
                      height="8"
                      style="border-radius: 30px !important"
                    />
                    <span class="stat-value">{{ formatMemory(calculateUsedMemory(instance.memory).average) }} GB</span>
                  </template>
                  <span>{{ formatMemory(calculateUsedMemory(instance.memory).average) }} GB used of
                    {{ formatMemory(convertToGB(instance.memory.total)) }} GB</span>
                </v-tooltip>
              </div>
              <div class="stat-minmax pt-1">
                Min: {{ formatMemory(calculateUsedMemory(instance.memory).min) }} GB |
                Max: {{ formatMemory(calculateUsedMemory(instance.memory).max) }} GB
              </div>
              <v-divider v-if="index < statistics.length - 1" class="my-2" />
            </div>
            <!-- Separator -->
            <v-divider class="my-2" />

            <!-- Database Settings Router Link -->
            <div class="d-flex align-center justify-space-between mb-2">
              <router-link
                v-if="frontendSettingId"
                :to="{ name: 'globalSettingsEdit', params: { id: frontendSettingId } }"
                class=" stat-title text-decoration-none text-success font-weight-bold"
              >
                {{ $lang.header.databaseSettings }}
              </router-link>
            </div>
            <div class="stat-title d-flex align-center">
              <div class="d-flex align-center">
                <v-icon color="success" class="me-1">
                  mdi-database
                </v-icon>
                {{ $lang.header.database }}
              </div>
              <div class="stat-minmax ms-1">
                ( {{ formatMemory(dbSizeGB) }} GB )
              </div>
            </div>
            <div class="stat-container">
              <template v-if="dbSizeGB">
                <div class="d-flex align-center w-100">
                  <v-tooltip location="top">
                    <template #activator="{ props }">
                      <v-progress-linear
                        class="flex-grow-1"
                        :model-value="usedDBMemoryGB"
                        :max="dbSizeGB"
                        color="success"
                        height="8"
                        v-bind="props"
                        style="border-radius: 30px !important; margin: 0 4px"
                      />
                    </template>
                    <span class="stat-value">
                      {{ formatMemory(usedDBMemoryGB) }} GB used of {{ formatMemory(dbSizeGB) }} GB
                    </span>
                  </v-tooltip>
                  <span class="stat-value ms-1">
                    {{ formatMemory(usedDBMemoryGB) }} GB
                  </span>
                </div>
              </template>

              <template v-else>
                <span class="stat-value">
                  <span class="text-subtitle-2">{{ $lang.header.usedDbMemory }}:</span>
                  {{ formatMemory(usedDBMemoryGB) }} GB
                </span>
              </template>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {
  getGlobalSettingsUsingGET as getSettings,
  getDatabaseStatisticUsingGET as getDatabaseStatistic,
  getStatisticUsingGET as getStatistic
} from "@/utils/api";
import {
  FLYOUT_OPEN_DELAY,
  FLYOUT_CLOSE_DELAY,
  FLYOUT_NAVIGATION_CLOSE_DELAY
} from '@/utils/constants';

export default {
  props: {
    rail: Boolean
  },
  data() {
    return {
      statistics: [],
      loading: true,
      error: false,
      roles: [],
      databaseStat: null,
      expandedPanels: [],
      intervalId: null,
      frontendSettingId: null,
      // Flyout state
      flyoutOpen: false,
      isTouchDevice: false,
      closeTimeout: null,
      // Expose constants to template
      FLYOUT_OPEN_DELAY,
      FLYOUT_CLOSE_DELAY
    };
  },
  computed: {
    ...mapState('app', ['userRoles']),
    dbSizeGB() {
      const used = this.databaseStat?.data?.usedMemory || 0;
      const available = this.databaseStat?.data?.availableMemory;

      if (available == null) return null;
      return this.convertToGB(used + available);
    },
    usedDBMemoryGB() {
      const bytes = this.databaseStat?.data?.usedMemory;
      return bytes ? this.convertToGB(bytes) : 0;
    },
    averageCpuUsage() {
      if (!this.statistics.length) return 0;
      const valid = this.statistics.filter(i => i.cpu?.average != null);
      if (!valid.length) return 0;
      return valid.reduce((sum, i) => sum + i.cpu.average, 0) / valid.length;
    },
    averageMemoryUsage() {
      const valid = this.statistics.filter(i => i.memory?.total != null && i.memory?.average != null);
      if (!valid.length) return 0;

      const totalMemory = valid.reduce((sum, i) => sum + this.convertToGB(i.memory.total), 0) / valid.length;
      const totalAverageMemory = valid.reduce((sum, i) => sum + this.convertToGB(i.memory.average), 0) / valid.length;
      const freeMemory = totalMemory - totalAverageMemory;
      return (freeMemory / totalMemory) * 100;
    },
    averageMemoryUsed() {
      const valid = this.statistics.filter(i => i.memory?.total != null && i.memory?.average != null);
      if (!valid.length) return 0;

      return valid.reduce((sum, i) => {
        const used = i.memory.total - i.memory.average;
        return sum + this.convertToGB(used);
      }, 0) / valid.length;
    }
  },
  watch: {
    '$route'() {
      if (this.flyoutOpen) {
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout)
        }
        this.closeTimeout = setTimeout(() => {
          this.flyoutOpen = false
          this.closeTimeout = null
        }, FLYOUT_NAVIGATION_CLOSE_DELAY)
      }
    }
  },
  created() {
    this.roles = this.userRoles.map((x) => x.name) || []
  },
  mounted() {
    // Touch device detection
    try {
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    } catch {
      this.isTouchDevice = false
    }

    const roles = this.roles;
    if (roles.includes('SUPER_USER') || roles.includes('INSTANCE_ADMIN')) {
      this.fetchStatistics();
      this.fetchDatabaseStat();
      this.fetchFrontendSettingId()
      this.intervalId = setInterval(() => {
        this.fetchStatistics();
        this.fetchDatabaseStat();
      }, 60000);
    }
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
    // Cleanup pending timeout
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  },
  methods: {
    ...mapActions('app', ['addSnackbar']),
    async fetchFrontendSettingId() {
      try {
        const response = await getSettings({ type: 'FRONTEND' });
        const setting = response?.data?.data?.[0];
        if (setting?.id) {
          this.frontendSettingId = setting.id;
        }
      } catch (error) {
        console.error("Failed to fetch frontend setting:", error);
      }
    },
    async fetchDatabaseStat() {
      try {
        const response = await getDatabaseStatistic()
        this.databaseStat = response?.data || response
      } catch (err) {
        console.error("Error fetching DB stats:", err);
      }
    },
    async fetchStatistics() {
      try {
        const response = await getStatistic({ request: '' });
        if (response.data.status === "SUCCESS") {
          this.statistics = response.data.data.items;
        } else {
          this.error = true;
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    calculateProgressCPU(metric) {
      if (!metric || typeof metric.average !== 'number') return 0;
      return metric.average;
    },
    calculateProgress(metric) {
      if (!metric || !metric.total || !metric.average) return 0;
      const used = metric.average;
      const total = metric.total;
      const free = total - used;
      return (free / total) * 100;
    },
    calculateUsedMemory(memory) {
      if (
        !memory ||
        memory.total == null ||
        memory.min == null ||
        memory.max == null ||
        memory.average == null
      ) {
        return { min: 0, max: 0, average: 0 };
      }

      return {
        min: this.convertToGB(memory.total - memory.max),
        max: this.convertToGB(memory.total - memory.min),
        average: this.convertToGB(memory.total - memory.average)
      };
    },
    convertToGB(bytes) {
      return bytes / (1024 * 1024 * 1024);
    },
    formatCpu(value) {
      return Number(value) % 1 === 0 ? Number(value).toFixed(0) : Number(value).toFixed(1);
    },
    formatMemory(value) {
      return Number(value).toFixed(2);
    },
    formatInstanceId(id) {
      return id ? id.slice(-4) : "????";
    }
  }
};
</script>

<style scoped>
::v-deep(.v-expansion-panel-text__wrapper) {
  padding: 8px 12px 16px !important;
}

::v-deep(.v-expansion-panel-title__icon) {
  margin-inline-start: 4px !important;
  font-size: 18px;
  color: rgb(var(--v-theme-customNavColor));
}

::v-deep(.v-expansion-panel-title--active .v-expansion-panel-title__icon) {
  color: rgb(var(--v-theme-customNavColor));
}

.rail-title.rail-active ::v-deep(.v-expansion-panel-title__icon) {
  display: none !important;
}
.rail-active {
  width: 97% !important;
}
</style>
